/**
 * Case Transformation Utilities
 *
 * Provides bidirectional transformation between snake_case (backend/Python)
 * and camelCase (frontend/TypeScript) naming conventions.
 *
 * Usage:
 * - Use transformKeys() to convert API responses to camelCase
 * - Use transformKeysToSnake() to convert request payloads to snake_case
 */

/**
 * Converts a snake_case string to camelCase
 * Handles both letters and numbers after underscores
 * Examples:
 *   total_services → totalServices
 *   trucks_due_within_2_weeks → trucksDueWithin2Weeks
 *   trucks_due_within_2_6_weeks → trucksDueWithin26Weeks
 */
export function snakeToCamel(str: string): string {
  return str.replace(/_([a-z0-9])/g, (_, char) => char.toUpperCase());
}

/**
 * Converts a camelCase string to snake_case
 */
export function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

/**
 * Recursively transforms object keys from snake_case to camelCase
 */
export function transformKeys<T = any>(obj: any): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => transformKeys(item)) as T;
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    return Object.keys(obj).reduce((result, key) => {
      const camelKey = snakeToCamel(key);
      result[camelKey] = transformKeys(obj[key]);
      return result;
    }, {} as any) as T;
  }

  return obj;
}

/**
 * Recursively transforms object keys from camelCase to snake_case
 */
export function transformKeysToSnake<T = any>(obj: any): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => transformKeysToSnake(item)) as T;
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    return Object.keys(obj).reduce((result, key) => {
      const snakeKey = camelToSnake(key);
      result[snakeKey] = transformKeysToSnake(obj[key]);
      return result;
    }, {} as any) as T;
  }

  return obj;
}

/**
 * Axios interceptor to transform response data from snake_case to camelCase
 */
export function createResponseTransformInterceptor() {
  return (response: any) => {
    if (response.data) {
      response.data = transformKeys(response.data);
    }
    return response;
  };
}

/**
 * Axios interceptor to transform request data from camelCase to snake_case
 */
export function createRequestTransformInterceptor() {
  return (config: any) => {
    if (config.data && typeof config.data === 'object') {
      config.data = transformKeysToSnake(config.data);
    }
    if (config.params && typeof config.params === 'object') {
      config.params = transformKeysToSnake(config.params);
    }
    return config;
  };
}
